import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, ChevronLeft, ChevronRight, Info, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import inventory from "@/api/inventory";

function fmtDate(s) {
  if (!s) return "—";
  return String(s).slice(0, 16).replace("T", " ");
}

function NeedsReviewModal({ open, onOpenChange, totalCount, apiOverride }) {
  const { t } = useTranslation();
  const api = apiOverride || inventory;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(totalCount || 0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [canonicalType, setCanonicalType] = useState("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api
      .getNeedsReview({
        page,
        pageSize,
        canonicalType: canonicalType === "all" ? null : canonicalType,
      })
      .then((d) => {
        if (!cancelled) {
          setItems(d.items || []);
          setTotal(d.total || 0);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error(
            t("inventory.needsReviewLoadError", {
              defaultValue: "No se pudo cargar la lista de revisión",
            })
          );
          console.error(e);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, page, pageSize, canonicalType, t]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="needs-review-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            {t("inventory.needsReviewTitle", { defaultValue: "Movimientos que requieren revisión" })}
            <Badge variant="secondary" className="ml-1">{total.toLocaleString("en-US")}</Badge>
          </DialogTitle>
          <DialogDescription>
            {t("inventory.needsReviewSubtitle", {
              defaultValue:
                "Lista read-only. Estos movimientos tienen patrones ambiguos y serán reclasificados manualmente en una fase posterior.",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-2">
          <Select value={canonicalType} onValueChange={(v) => { setCanonicalType(v); setPage(1); }}>
            <SelectTrigger className="h-8 w-[180px] text-sm" data-testid="needs-review-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("inventory.allTypes", { defaultValue: "Todos los tipos" })}</SelectItem>
              <SelectItem value="devolucion">devolucion</SelectItem>
              <SelectItem value="desconocido">desconocido</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-md border border-border overflow-hidden">
          <div className="overflow-x-auto max-h-[420px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Fecha</TableHead>
                  <TableHead className="text-xs">SKU</TableHead>
                  <TableHead className="text-xs">Tipo</TableHead>
                  <TableHead className="text-xs">Raw</TableHead>
                  <TableHead className="text-xs text-right">Qty</TableHead>
                  <TableHead className="text-xs">Folio</TableHead>
                  <TableHead className="text-xs">Source</TableHead>
                  <TableHead className="text-xs">Usuario</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && items.length === 0
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 8 }).map((__, j) => (
                          <TableCell key={j}><Skeleton className="h-3.5 w-full" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  : items.length === 0
                    ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground py-6">
                          {t("inventory.needsReviewEmpty", { defaultValue: "Sin movimientos a revisar" })}
                        </TableCell>
                      </TableRow>
                    )
                    : items.map((it, i) => (
                      <TableRow key={`${it.timestamp}-${it.sku}-${i}`} data-testid={`needs-review-row-${i}`}>
                        <TableCell className="text-xs tabular-nums">{fmtDate(it.timestamp)}</TableCell>
                        <TableCell className="text-xs font-mono">{it.sku}</TableCell>
                        <TableCell className="text-xs">
                          <Badge
                            variant={it.canonical_type === "devolucion" ? "secondary" : "outline"}
                            className="text-[10px]"
                          >
                            {it.canonical_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs font-mono text-muted-foreground">{it.raw_transaction_type}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{it.qty}</TableCell>
                        <TableCell className="text-xs font-mono">{it.folio || "—"}</TableCell>
                        <TableCell className="text-xs">{it.source}</TableCell>
                        <TableCell className="text-xs">{it.employee || "—"}</TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
          <span>
            {((page - 1) * pageSize + 1).toLocaleString("en-US")}–
            {Math.min(page * pageSize, total).toLocaleString("en-US")} {t("inventory.of", { defaultValue: "de" })} {total.toLocaleString("en-US")}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-7 w-7"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid="nr-page-prev">
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="tabular-nums px-2">{page} / {totalPages}</span>
            <Button variant="outline" size="icon" className="h-7 w-7"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    data-testid="nr-page-next">
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function NeedsReviewWidget({
  count,
  loading,
  testId = "needs-review-widget",
  apiOverride,
  onReviewClick,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (loading) {
    return <Skeleton className="h-24 w-full" data-testid={`${testId}-skeleton`} />;
  }

  const n = count || 0;
  const useNav = typeof onReviewClick === "function";

  return (
    <>
      <Card
        data-testid={testId}
        className="border-amber-300/60 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-900/40"
      >
        <CardContent className="p-3">
          <div className="flex items-start gap-2.5">
            <div className="shrink-0 rounded-md bg-amber-100 dark:bg-amber-900/40 p-1.5 mt-0.5">
              <AlertCircle className="h-4 w-4 text-amber-700 dark:text-amber-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] uppercase tracking-wide text-amber-700 dark:text-amber-300 font-semibold">
                  {t("inventory.needsReviewBadge", { defaultValue: "Requieren revisión" })}
                </span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3 w-3 text-amber-600/70 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[280px] text-xs">
                      {t("inventory.needsReviewTooltip", {
                        defaultValue:
                          "Movimientos con patrones ambiguos (devoluciones marcadas + desconocidos remanentes). Click 'Revisar' para reclasificar manualmente.",
                      })}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="text-2xl font-semibold tabular-nums leading-tight mt-0.5">
                {n.toLocaleString("en-US")}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {t("inventory.needsReviewSubcount", { defaultValue: "movimientos pendientes" })}
              </div>
            </div>
            <Button
              variant={useNav ? "default" : "outline"}
              size="sm"
              className="h-7 shrink-0"
              onClick={useNav ? onReviewClick : () => setOpen(true)}
              data-testid={useNav ? "needs-review-go-btn" : "needs-review-open-modal-btn"}
              disabled={n === 0}
            >
              {useNav
                ? t("inventory.review.entryButton", {
                    defaultValue: `Revisar (${n.toLocaleString("en-US")})`,
                    count: n.toLocaleString("en-US"),
                  })
                : t("inventory.viewList", { defaultValue: "Ver lista" })}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* The legacy read-only modal is only used when no navigation callback
          was provided (e.g. embedded inside the public-share view). */}
      {!useNav ? (
        <NeedsReviewModal
          open={open}
          onOpenChange={setOpen}
          totalCount={n}
          apiOverride={apiOverride}
        />
      ) : null}
    </>
  );
}
